import {
  AppShell as MantineAppShell,
  Burger,
  Center,
  Group,
  Image,
  Loader,
  Menu,
  Avatar,
  Text,
  UnstyledButton,
  NavLink,
  Stack,
  Divider,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  IconChevronDown,
  IconLogout,
  IconVideo,
  IconPlaylist,
  IconBroadcast,
  IconPlayerPlay,
  IconRss,
  IconApps,
  IconLayoutList,
  IconChartLine,
  IconHistory,
  IconCreditCard,
  IconChartBar,
  IconUsers,
  IconBuildingSkyscraper,
  IconUpload,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAccountPermissions } from '@/features/account/useAccountPermissions';
import { TrialHeaderNotice } from '@/features/account/components/billing/TrialHeaderNotice';
import { AccountClosedPage } from '@/features/account/components/closed/AccountClosedPage';
import { useLegacyReportsEligibility } from '@/features/legacy-reports/useLegacyReportsEligibility';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { useBillingPlan } from '@/features/account/hooks/useBilling';
import { hasAnyAppAccess } from '@lib2/model/customer/QuotaFeaturesAppUtil';
import { CustomerSelector } from '@/shared/components/CustomerSelector';
import { UploadButton } from '@/features/upload/components/UploadButton';
import { UploadHeaderIndicator } from '@/features/upload/components/UploadHeaderIndicator';
import { UploadDrawer } from '@/features/upload/components/UploadDrawer';
import { UploadEngineProvider } from '@/features/upload/UploadEngineProvider';
import { useAppRouteGuard } from './useAppRouteGuard';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { LinkUtils } from '@/features/link/LinkUtils';
import { ExtPlatConfig } from '@/shared/utils/extplatBrandConfig';
// 352x64 (2x of 176x32): original emblem + "streamhoster" wordmark lockup
import shHeaderLogo from '@/assets/img/shlogo_header.png';

export function AppShell() {
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { authData, logout, selectedCustomer, customerInfo, isUploadOnly, canViewMedia } = useAuth();
  const currentPath = routerState.location.pathname;
  const accountPerms = useAccountPermissions();
  const onUploadPage = currentPath === '/upload';

  // Account-closed gate (legacy App.doAfterCustomerUpdated parity): keyed off
  // the LOGIN account's state, not the selected sub-account. Closed accounts
  // get a takeover screen with no navbar — UX gating only; the backend remains
  // the authority on what an inactive account can actually call.
  const isAccountClosed = customerInfo?.customer.cfg.customerStateType === 'inactive';
  // Trial upgrade stays reachable while closed: upgrading IS the reactivation
  // path for expired trials (legacy AccountClosed → BillingUpgradeTrial).
  const showClosedScreen = isAccountClosed && !currentPath.startsWith('/account/upgrade-trial');

  // App-wide permission redirects (upload-only home, media-access gating).
  const { isRedirecting } = useAppRouteGuard(isAccountClosed);

  // Bootstrap billing plan early so trial/non-trial permissions resolve correctly.
  // Legacy code fetched this immediately after customer data loaded.
  // TanStack Query deduplicates — pages that also call useBillingPlan() share the cache.
  useBillingPlan();

  // Top-left header logo. Reseller (white-label) accounts show their own logo,
  // resolved to a signed CDN URL from the shared link template; direct customers
  // show the Streamhoster mark. Account-based (matches legacy isResellerExperience),
  // so a reseller account never flashes the Streamhoster mark while the config loads.
  const { data: linkConfig } = useSharedLinkConfig();
  const reseller = selectedCustomer?.cfg.reseller;
  const isResellerBrand = ExtPlatConfig.isReseller(reseller);
  const resellerLogoUrl =
    isResellerBrand &&
    linkConfig &&
    reseller &&
    reseller.logoFileId != null &&
    reseller.logoFormat != null
      ? LinkUtils.getParentAccountFileUrl(linkConfig, reseller.logoFileId, reseller.logoFormat)
      : null;

  const handleLogout = () => {
    logout();
    navigate({ to: '/login' });
  };

  const userName = authData?.cpuser?.core
    ? `${authData.cpuser.core.firstName} ${authData.cpuser.core.lastName}`
    : 'User';

  const userEmail = authData?.cpuser?.core?.email ?? '';

  const hasAccountSection =
    accountPerms.canViewBilling ||
    accountPerms.canViewUsage ||
    accountPerms.canViewTrialUpgrade ||
    accountPerms.canViewTeam ||
    accountPerms.canViewSubAccounts ||
    accountPerms.canViewUploadProfiles;

  const quotaApp = selectedCustomer?.cfg.quota.features.app;
  // Apps require media access too (legacy parity: APP tab needed service/subcustomer perms).
  const hasAppsSection = canViewMedia && !!quotaApp && hasAnyAppAccess(quotaApp);

  // Upload-only users with no account permissions get no navbar at all —
  // /upload IS their dashboard. With account perms they keep a minimal navbar.
  const hideNavbar = isAccountClosed || (isUploadOnly && !hasAccountSection);

  // Analytics nav gating is UX only — server-side authorization remains the
  // authority (gateway auth for /v1/analytics, platform auth for /reports1).
  const showAnalyticsNav =
    FEATURE_FLAGS.analyticsEnabled && !!authData?.cpuser.permissions.serviceRead;
  const { eligible: legacyReportsEligible } = useLegacyReportsEligibility();
  const hasAnalyticsSection = showAnalyticsNav || legacyReportsEligible;

  return (
    <UploadEngineProvider>
    <MantineAppShell
      header={{ height: 60 }}
      navbar={
        hideNavbar
          ? undefined
          : {
              width: 250,
              breakpoint: 'sm',
              collapsed: { mobile: !opened },
            }
      }
      padding="md"
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            {!hideNavbar && (
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label={opened ? 'Close navigation' : 'Open navigation'} />
            )}
            <Link
              to={isUploadOnly ? '/upload' : '/'}
              aria-label="Home"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            >
              {isResellerBrand ? (
                resellerLogoUrl ? (
                  // White-label logo rendered directly on the white header — no plate,
                  // keeping the design uncompromised. Reseller logos are being
                  // re-collected as light-surface artwork; legacy light-on-dark logos
                  // may appear faint until replaced.
                  <Image src={resellerLogoUrl} alt="" h={32} w="auto" maw={200} fit="contain" />
                ) : (
                  <Text fw={600} size="lg">
                    {selectedCustomer?.cfg.label ?? ''}
                  </Text>
                )
              ) : (
                <Image src={shHeaderLogo} alt="Streamhoster" h={32} w="auto" fit="contain" />
              )}
            </Link>
          </Group>

          <Group>
            <TrialHeaderNotice />
            {!isAccountClosed && <CustomerSelector />}
            {/* Drawer entry points hidden on /upload — the page IS the experience */}
            {!isAccountClosed && !onUploadPage && (
              <>
                <UploadHeaderIndicator />
                <UploadButton />
              </>
            )}

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Avatar size="sm" radius="xl" color="blue">
                      {userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Text size="sm" visibleFrom="sm">
                      {userName}
                    </Text>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>
                  {userEmail}
                </Menu.Label>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </MantineAppShell.Header>

      {!hideNavbar && (
      <MantineAppShell.Navbar p="md">
        {/* ScrollArea keeps all nav items reachable on short viewports */}
        <MantineAppShell.Section grow component={ScrollArea}>
        {/* UPLOAD home — only nav case is upload-only users WITH account perms */}
        {isUploadOnly && (
          <Stack gap={4}>
            <NavLink
              label="Upload"
              leftSection={<IconUpload size={18} />}
              active={onUploadPage}
              onClick={() => navigate({ to: '/upload' })}
            />
          </Stack>
        )}
        {/* MEDIA section — requires media access (serviceRead or subcustomer pair) */}
        {canViewMedia && (
          <>
        {hasAccountSection && (
          <Text size="xs" fw={500} c="dimmed" tt="uppercase" px="xs" mb={4}>
            Media
          </Text>
        )}
        <Stack gap={4}>
          {/* Order: content sources (Videos, Streams) → curation (Playlists)
              → presentation (Players) → syndication (Feeds) */}
          <NavLink
            label="Videos"
            leftSection={<IconVideo size={18} />}
            active={currentPath === '/videos' || currentPath.startsWith('/videos/')}
            onClick={() => navigate({ to: '/videos' })}
          />
          {selectedCustomer && selectedCustomer.cfg.quota.features.account.allowStream !== false && (
            <NavLink
              label="Streams"
              leftSection={<IconBroadcast size={18} />}
              active={currentPath === '/streams' || currentPath.startsWith('/streams/')}
              onClick={() => navigate({ to: '/streams' })}
            />
          )}
          <NavLink
            label="Playlists"
            leftSection={<IconPlaylist size={18} />}
            active={currentPath === '/playlists' || currentPath.startsWith('/playlists/')}
            onClick={() => navigate({ to: '/playlists' })}
          />
          <NavLink
            label="Players"
            leftSection={<IconPlayerPlay size={18} />}
            active={currentPath === '/players' || currentPath.startsWith('/players/')}
            onClick={() => navigate({ to: '/players' })}
          />
          {selectedCustomer && selectedCustomer.cfg.quota.features.account.allowFeed !== false && (
            <NavLink
              label="Feeds"
              leftSection={<IconRss size={18} />}
              active={currentPath === '/feeds' || currentPath.startsWith('/feeds/')}
              onClick={() => navigate({ to: '/feeds' })}
            />
          )}
        </Stack>
          </>
        )}

        {/* ANALYTICS section (Phase 5 Sequence 8; flag-gated) */}
        {hasAnalyticsSection && (
          <>
            <Divider my="sm" />
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" px="xs" mb={4}>
              Analytics
            </Text>
            <Stack gap={4}>
              {showAnalyticsNav && (
                <NavLink
                  label="Analytics"
                  leftSection={<IconChartLine size={18} />}
                  active={currentPath === '/analytics' || currentPath.startsWith('/analytics/')}
                  onClick={() => navigate({ to: '/analytics' })}
                />
              )}
              {legacyReportsEligible && (
                <NavLink
                  label="Legacy Reports"
                  leftSection={<IconHistory size={18} />}
                  active={currentPath.startsWith('/legacy-reports')}
                  onClick={() => navigate({ to: '/legacy-reports' })}
                />
              )}
            </Stack>
          </>
        )}

        {/* APPS section */}
        {hasAppsSection && (
          <>
            <Divider my="sm" />
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" px="xs" mb={4}>
              Apps
            </Text>
            <Stack gap={4}>
              <NavLink
                label="Apps"
                leftSection={<IconApps size={18} />}
                active={
                  (currentPath === '/apps' || currentPath.startsWith('/apps/')) &&
                  !currentPath.startsWith('/apps/content')
                }
                onClick={() => navigate({ to: '/apps' })}
              />
              <NavLink
                label="Content"
                leftSection={<IconLayoutList size={18} />}
                active={currentPath.startsWith('/apps/content')}
                onClick={() => navigate({ to: '/apps/content' })}
              />
            </Stack>
          </>
        )}

        {/* ACCOUNT section */}
        {hasAccountSection && (
          <>
            <Divider my="sm" />
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" px="xs" mb={4}>
              Account
            </Text>
            <Stack gap={4}>
              {accountPerms.canViewBilling && (
                <NavLink
                  label="Billing"
                  leftSection={<IconCreditCard size={18} />}
                  active={currentPath.startsWith('/account/billing')}
                  onClick={() => navigate({ to: '/account/billing' })}
                />
              )}
              {accountPerms.canViewUsage && (
                <NavLink
                  label="Usage"
                  leftSection={<IconChartBar size={18} />}
                  active={currentPath.startsWith('/account/usage')}
                  onClick={() => navigate({ to: '/account/usage' })}
                />
              )}
              {accountPerms.canViewTrialUpgrade && (
                <NavLink
                  label="Upgrade Plan"
                  leftSection={<IconCreditCard size={18} />}
                  active={currentPath.startsWith('/account/upgrade-trial')}
                  onClick={() => navigate({ to: '/account/upgrade-trial' })}
                />
              )}
              {accountPerms.canViewTeam && (
                <NavLink
                  label="Team"
                  leftSection={<IconUsers size={18} />}
                  active={currentPath.startsWith('/account/team')}
                  onClick={() => navigate({ to: '/account/team' })}
                />
              )}
              {accountPerms.canViewSubAccounts && (
                <NavLink
                  label="Sub-Accounts"
                  leftSection={<IconBuildingSkyscraper size={18} />}
                  active={currentPath.startsWith('/account/sub-accounts')}
                  onClick={() => navigate({ to: '/account/sub-accounts' })}
                />
              )}
              {accountPerms.canViewUploadProfiles && (
                <NavLink
                  label="Upload Profiles"
                  leftSection={<IconUpload size={18} />}
                  active={currentPath.startsWith('/account/upload-profiles')}
                  onClick={() => navigate({ to: '/account/upload-profiles' })}
                />
              )}
            </Stack>
          </>
        )}
        </MantineAppShell.Section>
      </MantineAppShell.Navbar>
      )}

      <MantineAppShell.Main
        style={{
          display: 'flex',
          flexDirection: 'column',
          // Definite height (not just minHeight) so the h="100%"/flex:1 chain
          // below resolves to bounded sizes — with an indefinite chain, the
          // virtualized grid scroll bodies fall back to content height (all
          // 33k rows) and virtualization breaks. Pages taller than the
          // viewport still overflow into normal window scrolling.
          height: '100dvh',
          minHeight: 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {showClosedScreen ? (
            <AccountClosedPage />
          ) : isRedirecting ? (
            <Center h="50vh">
              <Loader size="md" />
            </Center>
          ) : (
            <Outlet />
          )}
        </div>
      </MantineAppShell.Main>

      {/* Drawer suppressed on /upload — the page is the upload surface there */}
      {!isAccountClosed && !onUploadPage && <UploadDrawer />}
    </MantineAppShell>
    </UploadEngineProvider>
  );
}
